import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AuthContext';
import { 
  Home, 
  FileText, 
  Calendar, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  Coffee,
  MessageSquare,
  BookOpen,
} from 'lucide-react';

const menuItems = [
  { icon: Home, label: 'Dashboard', path: '/dashboard' },
  { icon: Calendar, label: 'Reservations', path: '/reservations' },
  { icon: FileText, label: 'Content Management', path: '/content' },
  { icon: BookOpen, label: 'Blog', path: '/blog' },
  // { icon: MessageSquare, label: 'Reviews', path: '/reviews' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export function Sidebar() {
  const { state, dispatch } = useApp();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const toggleSidebar = () => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  };

  const handleLogout = () => {
    dispatch({ type: 'AUTH_LOGOUT' });
    // Redirect to login will be handled by the app routing
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
        className={`fixed left-0 top-0 h-full transition-all duration-300 z-50 ${
          state.sidebarCollapsed ? 'w-16' : 'w-64'
        } hidden md:block`}
        style={{
          background: 'linear-gradient(180deg, #1f2937 0%, #111827 100%)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(75, 85, 99, 0.3)',
          borderRadius: '0 16px 16px 0'
        }}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-600/30">
          {!state.sidebarCollapsed && (
            <div className="flex items-center space-x-2">
              <img src="/logo.webp" alt="Logo" className="h-8 w-8" />
              <span className="text-xl font-bold text-white">GrandPittu</span>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-700/50 transition-all duration-200 text-gray-300 hover:text-white"
            style={{ borderRadius: '8px' }}
          >
            {state.sidebarCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isHovered = hoveredItem === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  relative flex items-center px-4 py-3 text-sm font-medium transition-all duration-200
                  ${isActive 
                    ? 'text-white bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/10 border-r-2 border-[#d4af37] shadow-lg' 
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/30'
                  }
                `}
                style={{ 
                  borderRadius: isActive ? '0 12px 12px 0' : '0 8px 8px 0',
                  marginRight: '8px'
                }}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <Icon className={`h-5 w-5 ${state.sidebarCollapsed ? '' : 'mr-3'}`} />
                {!state.sidebarCollapsed && (
                  <span className="truncate">{item.label}</span>
                )}
                
                {/* Tooltip for collapsed sidebar */}
                {state.sidebarCollapsed && isHovered && (
                  <div className="absolute left-full ml-2 px-2 py-1 text-black bg-white dark:bg-popover dark:text-popover-foreground text-xs rounded-md whitespace-nowrap z-50 shadow-lg">
                    {item.label}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="border-t border-border p-4 border-white dark:border-white">
          {!state.sidebarCollapsed && state.auth.user && (
            <div className="mb-3 text-white">
              <p className="text-sm font-medium text-foreground">{state.auth.user.name}</p>
              <p className="text-xs text-muted-foreground">{state.auth.user.email}</p>
            </div>
          )}
          
          <button
            onClick={handleLogout}
            className={`
              flex items-center w-full px-2 py-2 text-sm font-medium text-muted-foreground 
              hover:text-foreground hover:bg-muted rounded-md transition-colors
              ${state.sidebarCollapsed ? 'justify-center' : ''}
            `}
          >
            <LogOut className={`h-5 w-5 text-white ${state.sidebarCollapsed ? '' : 'mr-3'}`} />
            {!state.sidebarCollapsed && <span className='text-white'>Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div 
        className={`fixed left-0 top-0 h-full bg-card shadow-xl transition-transform duration-300 z-50 w-64 md:hidden ${
          state.sidebarCollapsed ? '-translate-x-full' : 'translate-x-0'
        }`}

        style={{
          background: 'linear-gradient(180deg, #1f2937 0%, #111827 100%)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(75, 85, 99, 0.3)',
          borderRadius: '0 16px 16px 0'
        }}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Coffee className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold text-white">grandPittu</span>
          </div>
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-gray-300 hover:text-white"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center px-4 py-3 text-sm font-medium transition-colors
                  ${isActive 
                     ? 'text-white bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/10 border-r-2 border-[#d4af37] shadow-lg' 
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/30'
                  }
                `}
                onClick={toggleSidebar} // Close mobile sidebar after navigation
              >
                <Icon className="h-5 w-5 mr-3" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="border-t border-border p-4 border-white dark:border-white">
          {state.auth.user && (
            <div className="mb-3 text-white">
              <p className="text-sm font-medium text-foreground">{state.auth.user.name}</p>
              <p className="text-xs text-muted-foreground">{state.auth.user.email}</p>
            </div>
          )}
          
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
          >
            <LogOut className="h-5 w-5 mr-3 text-white" />
            <span className="text-white">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}
